/* VialArt - "use my own background" for the CLASSIC builder (owner 2026-09-16:
   "add bg upload to the basic builder").

   Builder v3 has had this since Phase 3; the classic builder had only a LOGO
   upload and a whole-artwork upload. This is the v3 feature ported, and it is
   deliberately a SEPARATE asset: assets/va-builder2.js is a 194 KB minified
   file and the only change it needs is one call, so all the new code lives
   here where it is readable and can be reverted by removing one script tag.

   HOW IT REACHES THE PRESS (this is the part that matters):
     browser  window.vaBg.id ----> props._bg_upload_id on the cart line
                              ----> vp._bg_upload_id in the vault payload
     server   core/shopify_client validates + scans the id at ingest
              core/label_renderer._swap_bg_image swaps the SAME <image> href
   The server side is already builder-agnostic - it keys on _bg_upload_id and
   never asks which builder produced it - so nothing server-side changed.
   A background that previews but never reaches the press is exactly the silent
   data-loss bug that cost order #1788 its logo; if you edit this file, keep the
   cart property and the vault payload in step with the preview.

   ORDERING IS LOAD-BEARING: vaBgSwap must run BEFORE vaApplyMaterial, so the
   customer photo sits UNDER the holographic stock texture, not over it. That is
   why va-builder2.js calls it where it does.
*/
(function () {
  'use strict';

  /* the one piece of shared state; read by va-builder2.js and by the cart
     property assembly in sections/vialart.liquid */
  window.vaBg = window.vaBg || { id: '', dataUrl: '', name: '' };

  function die() {
    return (window.VA_DIE === '10ml') ? '10ml' : '3ml';
  }
  function dieBox() {
    return (die() === '10ml') ? { w: 63.5, h: 25.4 } : { w: 44.958, h: 19.05 };
  }

  /* ---------------------------------------------------------------- swap ---
     EXACT twin of builder v3's bgSwap and of the server's _swap_bg_image.

     KEEP THE ATTRIBUTE NAME. Every live beta_*.svg writes xlink:href; rewriting
     it to a bare href only survives an HTML parser. Anything that re-parses the
     SVG as XML (a Blob loaded through <img>, which is how the cart thumbnail is
     produced) drops a bare href on <image> because it is SVG2-only - and the
     blank-background JPEG is what would then become the customer's cart image.

     Function replacer, never a string: a data: URL must never be read for
     $-patterns. */
  window.vaBgSwap = function (svg) {
    try {
      if (!svg || !window.vaBg || !window.vaBg.dataUrl) return svg;
      var m = /<image\b[^>]*>/i.exec(svg);
      if (!m) return svg;
      var tag = m[0], du = window.vaBg.dataUrl, hit = false;
      var out = tag.replace(/(\s(?:xlink:)?href\s*=\s*)(?:"[^"]*"|'[^']*')/i,
        function (_m, pre) { hit = true; return pre + '"' + du + '"'; });
      if (!hit) return svg;
      return svg.slice(0, m.index) + out + svg.slice(m.index + tag.length);
    } catch (e) { return svg; }
  };

  /* Some designs are drawn with no artwork at all (pure vector), so there is no
     <image> to replace and the control must say so rather than silently no-op. */
  function capable() {
    try {
      if (typeof window.buildLabel !== 'function') return true;  /* unknown -> allow */
      var d = (typeof window.vaCurrentDesign === 'function') ? window.vaCurrentDesign() : null;
      if (!d) return true;
      var svg = window.buildLabel(d, {}, false);
      return !!(svg && /<image\b/i.test(svg));
    } catch (e) { return true; }
  }

  function rerender() {
    try { if (typeof window.renderAll === 'function') { window.renderAll(); return; } } catch (e) {}
    try { if (typeof window.updateFlat === 'function') window.updateFlat(); } catch (e) {}
    try { if (typeof window.scheduleVial === 'function') window.scheduleVial(); } catch (e) {}
  }

  /* ------------------------------------------------------------------ UI ---
     Injected right after the logo row so the two "bring your own" controls sit
     together. Built in JS rather than Liquid so the whole feature is one file. */
  var CSS =
    '#va-bgup{margin-top:12px;}' +
    '#va-bgup .va-bgrow{display:flex;align-items:center;gap:10px;flex-wrap:wrap;}' +
    '#va-bg-pick{background:rgba(124,77,255,.10);border:1px solid rgba(124,77,255,.42);' +
      'border-radius:11px;color:#cdc4e8;font:inherit;font-weight:800;font-size:13px;' +
      'padding:11px 14px;cursor:pointer;min-height:44px;}' +
    '#va-bg-pick:hover:not(:disabled){background:rgba(124,77,255,.18);border-color:#8f66ff;}' +
    '#va-bg-pick:disabled{opacity:.55;cursor:default;}' +
    '#va-bg-name{font-size:12.5px;color:var(--muted,#9a93b8);}' +
    '#va-bg-rm{background:transparent;border:1px solid var(--line,#2b2748);border-radius:10px;' +
      'color:#d8d3ee;font:inherit;font-size:12.5px;padding:9px 12px;cursor:pointer;min-height:38px;}' +
    '#va-bg-msg{margin-top:7px;font-size:12px;line-height:1.5;}' +
    '#va-bg-msg.err{color:#ff9f96;font-weight:700;}' +
    '#va-bg-msg.warn{color:#ffd97a;}' +
    '#va-bg-acks{margin-top:8px;font-size:12px;color:#c9c4e0;}' +
    '#va-bg-acks label{display:flex;gap:8px;align-items:flex-start;margin:6px 0;cursor:pointer;}' +
    '#va-bg-acks input{margin-top:2px;accent-color:#7c4dff;flex:none;}' +
    '#va-bg-go{margin-top:8px;background:linear-gradient(120deg,#6d3bff,#9b5cff);border:0;' +
      'border-radius:11px;color:#fff;font:inherit;font-weight:800;font-size:13.5px;' +
      'padding:11px 16px;cursor:pointer;min-height:44px;}' +
    '#va-bg-go:disabled{opacity:.6;cursor:default;}';

  var HTML =
    '<div class="va-field" id="va-bgup">' +
      '<div class="va-bgrow">' +
        '<input type="file" id="va-bg-file" hidden accept="image/png,image/jpeg" ' +
          'aria-label="Choose your own background image (PNG or JPG)">' +
        '<button type="button" id="va-bg-pick">Use my own background</button>' +
        '<span id="va-bg-name">PNG or JPG &middot; 200 DPI minimum at label size</span>' +
        '<button type="button" id="va-bg-rm" style="display:none">Remove</button>' +
      '</div>' +
      '<div id="va-bg-msg" hidden></div>' +
      '<div id="va-bg-acks" hidden></div>' +
    '</div>';

  function $(id) { return document.getElementById(id); }

  function msg(text, kind) {
    var el = $('va-bg-msg');
    if (!el) return;
    el.className = kind || '';
    el.textContent = text || '';
    el.hidden = !text;
  }

  function paint() {
    var pick = $('va-bg-pick'), nm = $('va-bg-name'), rm = $('va-bg-rm');
    if (!pick) return;
    var on = !!(window.vaBg && window.vaBg.id);
    pick.textContent = on ? 'Replace background' : 'Use my own background';
    if (rm) rm.style.display = on ? '' : 'none';
    if (nm) {
      nm.textContent = on
        ? (window.vaBg.name || 'your image')
        : 'PNG or JPG · 200 DPI minimum at label size';
    }
    var cap = capable();
    pick.disabled = !cap;
    if (!cap && nm) {
      nm.textContent = 'This design has no background image to replace — '
        + 'pick another design to use your own.';
    }
  }
  window.vaBgPaint = paint;

  function clear() {
    window.vaBg = { id: '', dataUrl: '', name: '' };
    var acks = $('va-bg-acks');
    if (acks) { acks.innerHTML = ''; acks.hidden = true; }
    msg('');
    paint();
    rerender();
  }

  function applyNow(file, res, id) {
    window.vaBg = { id: id, dataUrl: res.dataUrl || '', name: file.name || '' };
    var acks = $('va-bg-acks');
    if (acks) { acks.innerHTML = ''; acks.hidden = true; }
    msg('');
    paint();
    rerender();
  }

  /* scan -> upload -> apply. The scan is a CONTENT gate and runs BEFORE the R2
     POST; upload is R2 only, there is deliberately no Shopify-file fallback. */
  function scanUploadApply(file, res) {
    var pick = $('va-bg-pick');
    var label = pick ? pick.textContent : '';
    if (pick) { pick.disabled = true; pick.textContent = 'Checking artwork…'; }
    return window.vaArtCheck.scan(file).then(function (sc) {
      /* a finished LABEL uploaded as a BACKGROUND: hand the file to the label
         uploader instead of burying their text under our layout */
      if (sc && sc.label_like && typeof window.vaHandoffToUploader === 'function') {
        window.vaHandoffToUploader(file, 'bg-looks-like-label');
        var e = new Error('VA_LABEL_NOT_BG'); e.vaLabel = true; throw e;
      }
      if (pick) pick.textContent = 'Uploading…';
      return window.vaArtCheck.upload(file, die());
    }).then(function (u) {
      if (!u || !u.id) throw new Error('upload returned no id');
      applyNow(file, res, u.id);
      if (pick) { pick.disabled = false; pick.textContent = label; }
    })['catch'](function (err) {
      if (pick) { pick.disabled = false; pick.textContent = label; }
      if (err && err.vaLabel) return;            /* already handed off */
      if (err && err.vaBlocked) {
        msg('That image cannot be used as a background. Please choose another.', 'err');
        return;
      }
      msg((err && err.message) ? err.message : 'Upload failed - please try again.', 'err');
    });
  }

  function choose(file) {
    if (!file) return;
    msg('');
    var acks = $('va-bg-acks');
    if (acks) { acks.innerHTML = ''; acks.hidden = true; }
    if (!window.vaArtCheck || !window.vaArtCheck.validate) {
      msg('Artwork checker is still loading - try again in a moment.', 'err');
      return;
    }
    window.vaArtCheck.validate(file, { die: die(), mode: 'background', box: dieBox() })
      .then(function (res) {
        if (!res) { msg('Could not read that image.', 'err'); return; }
        if (res.hard && res.hard.length) {
          msg(res.hard.map(function (h) { return h.text; }).join(' '), 'err');
          return;
        }
        var warns = (res.warns || []).filter(function (w) { return w.tier !== 'pass'; });
        var needAck = (res.acks && res.acks.length) || warns.length;
        if (!needAck) { scanUploadApply(file, res); return; }
        /* something to tell them: show it and make them confirm, the same
           contract v3 uses - never silently accept a flagged image */
        if (warns.length) msg(warns.map(function (w) { return w.text; }).join(' '), 'warn');
        if (acks) {
          acks.hidden = false;
          acks.innerHTML =
            (res.acks || []).map(function (a, i) {
              return '<label><input type="checkbox" data-ack="' + i + '">' +
                     '<span>' + (a.label || '') +
                     (a.sub ? '<br><span style="opacity:.75">' + a.sub + '</span>' : '') +
                     '</span></label>';
            }).join('') +
            '<button type="button" id="va-bg-go">Use this image</button>';
          var go = $('va-bg-go');
          var boxes = acks.querySelectorAll('input[data-ack]');
          function sync() {
            var all = true;
            Array.prototype.forEach.call(boxes, function (b) { if (!b.checked) all = false; });
            if (go) go.disabled = !all;
          }
          Array.prototype.forEach.call(boxes, function (b) { b.addEventListener('change', sync); });
          sync();
          if (go) {
            go.addEventListener('click', function () {
              if (go.disabled) return;
              go.disabled = true;
              scanUploadApply(file, res);
            });
          }
        }
      })['catch'](function () {
        msg('Could not read that image - please try a different file.', 'err');
      });
  }

  function mount() {
    if ($('va-bgup')) return true;
    var logo = document.getElementById('va-logo-file');
    var host = logo ? logo.closest('.va-field') : null;
    if (!host || !host.parentNode) return false;
    var st = document.createElement('style');
    st.id = 'va-bg-css';
    st.textContent = CSS;
    document.head.appendChild(st);
    var wrap = document.createElement('div');
    wrap.innerHTML = HTML;
    var node = wrap.firstChild;
    host.parentNode.insertBefore(node, host.nextSibling);

    $('va-bg-pick').addEventListener('click', function () {
      var f = $('va-bg-file');
      if (f) f.click();
    });
    $('va-bg-file').addEventListener('change', function (e) {
      var f = e.target.files && e.target.files[0];
      e.target.value = '';                       /* re-picking the same file must fire */
      choose(f);
    });
    $('va-bg-rm').addEventListener('click', clear);
    paint();
    return true;
  }

  var tries = 0;
  (function tick() {
    if (mount() || tries++ > 60) return;
    setTimeout(tick, 250);
  })();

  /* the design picker can change whether a background is even replaceable */
  try {
    document.addEventListener('click', function (e) {
      var t = e.target && e.target.closest ? e.target.closest('[data-design],.va-di,.va-dtile') : null;
      if (t) setTimeout(paint, 250);
    });
  } catch (e) {}
})();
